import { useState } from "react";
import EditableTagListCell from "../../../../../components/custom-table/tableCell/cellTypes/EditableTagListCell";
import ChevronDownSm from "../../../../../svg/chevron-down-sm";
import CollapsibleSection from "./CollapsibleSection";

/** Tags shown before the list collapses into a "+N more" affordance. */
const MAX_VISIBLE_MEMBERS = 6;

/**
 * Props for a collapsible, editable member tag list.
 */
interface MemberTagsSectionProps {
  /** Section label (e.g. Members, or a dose tier name). */
  title: string;
  /** Stable id used to remount the tag editor when the row changes. */
  rowId: string;
  /** Member drug names. */
  members: string[];
  /** Placeholder for the inline add input. */
  placeholder?: string;
  /** Receives the full next list after an add or delete. */
  onChange: (members: string[]) => void;
}

/**
 * Collapsible member list rendered with the Oasis editable tag list, which
 * provides deletable badges, inline add, and overflow collapsing.
 *
 * @param props - Section label, members, and change handler
 * @returns Member section for a family card
 */
const MemberTagsSection = ({
  title,
  rowId,
  members,
  placeholder = "Drug",
  onChange,
}: MemberTagsSectionProps) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        className="flex w-fit flex-row items-center gap-2 text-left text-tiny font-semibold uppercase text-primaryGray-7"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span
          className={`flex transition-transform duration-300 ease-in-out ${
            expanded ? "rotate-0" : "-rotate-90"
          }`}
        >
          <ChevronDownSm />
        </span>
        {title}
        <span className="font-normal normal-case">({members.length})</span>
      </button>

      <CollapsibleSection expanded={expanded} className="pl-4">
        <EditableTagListCell
          value={{
            values: members,
            placeholder,
            emptyLabel: "No members",
            maxVisibleTags: MAX_VISIBLE_MEMBERS,
          }}
          rowData={{ id: rowId }}
          header={title}
          onCellValueChange={(_rowData, _fieldKey, newValue) =>
            onChange(Array.isArray(newValue) ? (newValue as string[]) : [])
          }
        />
      </CollapsibleSection>
    </div>
  );
};

export default MemberTagsSection;
